// frontend/src/pages/recruiter/RecruiterDashboard.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "./../../components/ui/card";
import { Badge } from "./../../components/ui/badge";
import { Button } from "./../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./../../components/ui/tabs";
import { Alert, AlertDescription } from "./../../components/ui/alert";
import Loader from "./../../components/ui/Loader";
import {
  Briefcase,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Edit,
  Trash2,
  Eye,
  BarChart3,
  Plus,
  MapPin,
  Calendar,
} from "lucide-react";
import api from "./../../api/client";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { isPublishedJob } from "../../utils/jobSlug";
import { glassCard, glassCardHover, glassPanel } from "../../utils/glassStyles";
import RecruiterPageShell from "../../components/recruiter/RecruiterPageShell";
import RecruiterStatCard from "../../components/recruiter/RecruiterStatCard";
import JobStatusBadge from "../../components/recruiter/JobStatusBadge";
import RecruiterEmptyState from "../../components/recruiter/RecruiterEmptyState";

export default function RecruiterDashboard() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const res = await api.get("/recruiter/jobs");
      setJobs(res.data);
    } catch (err) {
      setError("Failed to load your jobs");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await api.delete(`/recruiter/jobs/${jobId}`);
      setSuccess("Job deleted successfully");
      fetchJobs();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Failed to delete job");
    }
  };

  const totalJobs = jobs.length;
  const totalApplications = jobs.reduce((sum, job) => sum + (job.applicationCount || 0), 0);
  const pendingApps = jobs.reduce((sum, job) => sum + (job.pendingCount || 0), 0);
  const acceptedApps = jobs.reduce((sum, job) => sum + (job.acceptedCount || 0), 0);
  const pendingJobs = jobs.filter((j) => j.status === "PENDING").length;
  const publishedJobs = jobs.filter((j) => isPublishedJob(j)).length;

  if (loading) return <Loader label="Loading your dashboard..." />;

  return (
    <RecruiterPageShell
      title="Recruiter Dashboard"
      subtitle="Manage your jobs and track applications"
      actions={
        <Button onClick={() => navigate("/recruiter/post-job")} size="lg" className="shadow-lg">
          <Plus className="w-4 h-4 mr-2" />
          Post New Job
        </Button>
      }
    >
      {success && (
        <Alert className="mb-6 border-primary/30 bg-primary/10">
          <CheckCircle className="h-4 w-4 text-primary" />
          <AlertDescription className="text-primary dark:text-accent">{success}</AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-8">
        <RecruiterStatCard
          title="Total Jobs"
          value={totalJobs}
          hint={`${pendingJobs} pending approval`}
          icon={Briefcase}
          accent="blue"
          onClick={() => navigate("/recruiter/my-jobs")}
        />
        <RecruiterStatCard
          title="Total Applications"
          value={totalApplications}
          hint="Across all jobs"
          icon={BarChart3}
          accent="purple"
          onClick={() => navigate("/recruiter/applications/analytics")}
        />
        <RecruiterStatCard
          title="Pending Review"
          value={pendingApps}
          hint="Applications awaiting action"
          icon={Users}
          accent="orange"
          onClick={() => navigate("/recruiter/applications")}
        />
        <RecruiterStatCard
          title="Accepted"
          value={acceptedApps}
          hint="Successful candidates"
          icon={CheckCircle}
          accent="green"
          onClick={() => navigate("/recruiter/applications/accepted")}
        />
      </div>

      <div className={`${glassPanel} p-4 sm:p-6`}>
        <Tabs defaultValue="all" className="w-full">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <TabsList className="h-11 w-full sm:w-auto grid grid-cols-3 bg-white/10 dark:bg-white/5 backdrop-blur-sm">
              <TabsTrigger value="all">All ({totalJobs})</TabsTrigger>
              <TabsTrigger value="pending">Pending ({pendingJobs})</TabsTrigger>
              <TabsTrigger value="approved">Live ({publishedJobs})</TabsTrigger>
            </TabsList>
          </div>

          {["all", "pending", "approved"].map((tab) => {
            const filteredJobs =
              tab === "all"
                ? jobs
                : tab === "pending"
                  ? jobs.filter((j) => j.status === "PENDING")
                  : jobs.filter((j) => isPublishedJob(j));

            return (
              <TabsContent key={tab} value={tab} className="space-y-4 mt-0">
                {filteredJobs.length === 0 ? (
                  <RecruiterEmptyState
                    icon={Briefcase}
                    title={
                      tab === "all"
                        ? "No jobs yet"
                        : tab === "pending"
                          ? "No pending jobs"
                          : "No published jobs yet"
                    }
                    description={
                      tab === "all"
                        ? "Start by posting your first job opening to attract talented candidates."
                        : tab === "pending"
                          ? "All your jobs have been reviewed."
                          : "Post a job to start receiving applications."
                    }
                    actionLabel={tab === "all" ? "Post Your First Job" : undefined}
                    onAction={tab === "all" ? () => navigate("/recruiter/post-job") : undefined}
                  />
                ) : (
                  filteredJobs.map((job) => (
                    <Card key={job.id} className={`overflow-hidden rounded-2xl ${glassCardHover}`}>
                      <div className="flex flex-col md:flex-row">
                        <CardContent className="flex-1 p-5 sm:p-6">
                          <div className="flex flex-wrap items-center gap-2 mb-3">
                            <h3
                              className="text-xl font-bold cursor-pointer hover:text-primary transition-colors"
                              onClick={() => navigate(`/recruiter/jobs/${job.id}`)}
                            >
                              {job.title}
                            </h3>
                            <JobStatusBadge status={job.status} job={job} />
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {job.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(job.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2 mb-4">
                            <Badge variant="outline" className="bg-yellow-500/10 border-yellow-300/40 text-yellow-800 dark:text-yellow-300">
                              <Clock className="w-3 h-3 mr-1" />
                              {job.pendingCount || 0} Pending
                            </Badge>
                            <Badge variant="outline" className="bg-green-500/10 border-green-300/40 text-green-800 dark:text-green-300">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              {job.acceptedCount || 0} Accepted
                            </Badge>
                            <Badge variant="outline" className="bg-red-500/10 border-red-300/40 text-red-800 dark:text-red-300">
                              <XCircle className="w-3 h-3 mr-1" />
                              {job.rejectedCount || 0} Rejected
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button size="sm" onClick={() => navigate(`/recruiter/jobs/${job.id}/applicants`)}>
                              <Users className="w-4 h-4 mr-2" />
                              Applicants
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => navigate(`/recruiter/jobs/${job.id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Details
                            </Button>
                            {job.status === "PENDING" && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => navigate(`/recruiter/jobs/${job.id}/edit`)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </Button>
                                <ConfirmDialog
                                  trigger={
                                    <Button size="sm" variant="outline" className="text-destructive hover:text-destructive">
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </Button>
                                  }
                                  title="Delete Job"
                                  description="Are you sure you want to delete this job? This action cannot be undone."
                                  onConfirm={() => handleDeleteJob(job.id)}
                                  confirmText="Delete"
                                  variant="destructive"
                                />
                              </>
                            )}
                          </div>
                        </CardContent>
                        <div className={`md:min-w-[140px] p-5 sm:p-6 flex items-center justify-center border-t md:border-t-0 md:border-l border-white/10 ${glassCard}`}>
                          <div className="text-center">
                            <div className="text-4xl font-bold text-primary">{job.applicationCount || 0}</div>
                            <p className="text-xs text-muted-foreground font-medium mt-1">Applications</p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </RecruiterPageShell>
  );
}
